// A draft saved on this computer, as a Markdown file: the draft's own text, so its formatting
// comes back when it's opened again, and any other editor can open it too.
//
// Where the browser can (Chrome and Edge), the writer chooses where the file goes, and saving
// again writes to the same file. Elsewhere, saving downloads the file, and opening reads one.

import { MAX_DRAFT_NAME_LENGTH } from '@lyrmo/shared';
import { downloadText } from '../../lib/download';
import { plainText } from './markdown';

/** What the browser's file picker offers when opening a draft. */
export const DRAFT_FILE_TYPES = [
  {
    description: 'Markdown',
    accept: { 'text/markdown': ['.md', '.markdown'], 'text/plain': ['.txt'] },
  },
] as const;

/** A file the page may write again, from the browser's own pickers. */
export interface DraftFileHandle {
  readonly name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<{ write(text: string): Promise<void>; close(): Promise<void> }>;
  queryPermission?(options: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission?(options: { mode: 'readwrite' }): Promise<PermissionState>;
}

interface FilePickers {
  showOpenFilePicker?(options: {
    types: typeof DRAFT_FILE_TYPES;
    excludeAcceptAllOption?: boolean;
  }): Promise<DraftFileHandle[]>;
  showSaveFilePicker?(options: {
    suggestedName: string;
    types: typeof DRAFT_FILE_TYPES;
  }): Promise<DraftFileHandle>;
}

const pickers = (): FilePickers => window as unknown as FilePickers;

/** Whether the browser lets the writer choose where a file goes, and the page save to it again. */
export function canPickFiles(): boolean {
  const browser = pickers();
  return (
    typeof browser.showOpenFilePicker === 'function' &&
    typeof browser.showSaveFilePicker === 'function'
  );
}

/** Whether `error` is the writer closing a picker without choosing a file. */
const cancelled = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';

// Characters file systems refuse in a name, and those that hide in one.
// eslint-disable-next-line no-control-regex
const UNSAFE = /[\\/:*?"<>|\u0000-\u001f\u007f]+/g;

/** A file name for a draft called `name`, safe on every system, such as “Cover letter.md”. */
export function draftFileName(name: string, fallback: string): string {
  const safe = name.replace(UNSAFE, ' ').replace(/\s+/g, ' ').trim().replace(/^\.+/, '');
  return `${safe.slice(0, MAX_DRAFT_NAME_LENGTH).trim() || fallback}.md`;
}

/** What a draft opened from a file is called: the file's name without its extension. */
export const draftNameOf = (fileName: string): string =>
  fileName
    .replace(/\.(md|markdown|txt)$/i, '')
    .slice(0, MAX_DRAFT_NAME_LENGTH)
    .trim();

/**
 * A name for a draft not saved before: its first line, as it reads, up to a word that ends
 * before the length a name can have. Empty when the draft has no words.
 */
export function suggestName(text: string): string {
  // The start is enough to find the first line; reading it all could be slow.
  const line = plainText(text.slice(0, 2_000))
    .split('\n')
    .map((each) => each.replace(/\s+/g, ' ').trim())
    .find(Boolean);
  if (!line) return '';
  if (line.length <= 60) return line;
  const cut = line.slice(0, 60);
  const space = cut.lastIndexOf(' ');
  return `${(space > 20 ? cut.slice(0, space) : cut).replace(/[\s,.;:–—-]+$/u, '')}…`;
}

/**
 * Asks where to save a draft, and saves it there, or else downloads it. Returns the file to
 * save to again, null when it was downloaded, or undefined when the writer cancelled.
 */
export async function saveDraftFile(
  fileName: string,
  text: string,
): Promise<DraftFileHandle | null | undefined> {
  // Called on the window, as the browser's own methods must be.
  const browser = pickers();
  if (!canPickFiles() || !browser.showSaveFilePicker) {
    downloadText(fileName, text, 'text/markdown');
    return null;
  }
  let handle: DraftFileHandle;
  try {
    handle = await browser.showSaveFilePicker({
      suggestedName: fileName,
      types: DRAFT_FILE_TYPES,
    });
  } catch (error) {
    if (cancelled(error)) return undefined;
    throw error;
  }
  await writeDraftFile(handle, text);
  return handle;
}

/** Saves the draft to the file it was saved to, or opened from, before. */
export async function writeDraftFile(handle: DraftFileHandle, text: string): Promise<void> {
  // A file that was opened has to be allowed to be written, which the browser asks about.
  if (
    handle.queryPermission &&
    (await handle.queryPermission({ mode: 'readwrite' })) !== 'granted'
  ) {
    if ((await handle.requestPermission?.({ mode: 'readwrite' })) !== 'granted') {
      throw new DOMException('Not allowed to save to this file.', 'NotAllowedError');
    }
  }
  const writable = await handle.createWritable();
  await writable.write(text);
  await writable.close();
}

/**
 * Asks the browser's picker for a draft file, where it has one that can save to it again: the
 * file and that handle, or null when cancelled. Undefined where there's no such picker, so the
 * page asks with a file input instead.
 */
export async function pickDraftFile(): Promise<
  { readonly file: File; readonly handle: DraftFileHandle } | null | undefined
> {
  const browser = pickers();
  if (!canPickFiles() || !browser.showOpenFilePicker) return undefined;
  try {
    const [handle] = await browser.showOpenFilePicker({ types: DRAFT_FILE_TYPES });
    return handle ? { file: await handle.getFile(), handle } : null;
  } catch (error) {
    if (cancelled(error)) return null;
    throw error;
  }
}
